import { FormikProvider, useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { Loader } from 'app/shared/loader/loader';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import React from 'react';
import {
  fetchAccessibilityAppliedMeasureQuery,
  fetchAccessibilityRatingQuery, fetchBuildingPurposeByTypeQuery,
  fetchBuildingTypeNationalClassificationForCBPTypeQuery,
  fetchConstructionBuildingProcedureByTypeCodebookQuery,
  fetchConstructionCommencementQuery,
  fetchGreenInfrastructureTypeQuery,
  fetchRequirementGroupQuery,
} from 'app/edozvola/codebooks/codebooks.queries';
import { ConstructionBuildingProcedureType } from 'app/edozvola/codebooks/model/construction-building-procedure-codebook.model';
import { queryClient } from 'app/app';
import {
  Construction,
  initialConstruction,
} from 'app/edozvola/cases/construction-building-procedure/constructions/model/construction.model';
import { fetchConstructionQuery, saveConstructionQuery } from '../service/construction-building-procedure.queries';
import { NumericFormat } from 'react-number-format';
import { constructionValidationV2Schema } from './construction.schema';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";
import {CASE_TAB_ENUM} from "app/config/constants";
import { revalidateTabsThunk } from 'app/shared/reducers/validationerrors/validation-errors.reducer';
import getStore from 'app/config/store';

export interface IConstructionFormV2Props {
  idConstruction: string | null;
  setIdConstruction: any;
  readOnly: boolean;
  variant: string;
}

const ConstructionFormV2 = (props: IConstructionFormV2Props) => {
  const { id: uuid } = useParams<{ id: string }>();
  const {
    data: constructionData,
    isLoading: constructionDataLoading
  } = fetchConstructionQuery(props.idConstruction, props.variant);

  const { data: constructionBuildingProcedureOptions, isLoading: constructionBuildingProcedureOptionsLoading } =
    fetchConstructionBuildingProcedureByTypeCodebookQuery(uuid, ConstructionBuildingProcedureType.GRADEVINA);

  const { data: buildingPurposeOptions, isLoading: buildingPurposeOptionsLoading } = fetchBuildingPurposeByTypeQuery(ConstructionBuildingProcedureType.GRADEVINA);
  const { data: requirementGroupOptions, isLoading: requirementGroupOptionsLoading } = fetchRequirementGroupQuery();
  const {
    data: constructionCommencementOptions,
    isLoading: constructionCommencementOptionsLoading
  } = fetchConstructionCommencementQuery();
  const {
    data: buildingTypeNationalClassificationOptions,
    isLoading: buildingTypeNationalClassificationOptionsLoading
  } =
    fetchBuildingTypeNationalClassificationForCBPTypeQuery(ConstructionBuildingProcedureType.GRADEVINA);
  const {
    data: accessibilityRatingOptions,
    isLoading: accessibilityRatingOptionsLoading
  } = fetchAccessibilityRatingQuery();
  const { data: accessibilityAppliedMeasureOptions, isLoading: accessibilityAppliedMeasureOptionsLoading } =
    fetchAccessibilityAppliedMeasureQuery();
  const { data: greenInfrastructureTypeOptions, isLoading: greenInfrastructureTypeOptionsLoading } = fetchGreenInfrastructureTypeQuery();

  const dispatch = getStore().dispatch;

  const formik = useFormik({
    initialValues: constructionData || initialConstruction,
    validationSchema: constructionValidationV2Schema,
    onSubmit: (values: Construction) => {
      saveConstructionMutation(values);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const {
    mutate: saveConstructionMutation,
    isPending: isConstructionSaving
  } = saveConstructionQuery(uuid, props.variant, () => {
    closeModal();
  });

  const closeModal = () => {
    props.setIdConstruction(null);
    queryClient.invalidateQueries({ queryKey: ['construction'] });
    dispatch(revalidateTabsThunk());
  };

  const openIspuLocator = (buildingUUid: string) => {
    const url = window.location.origin + `/ispu-locator/constructions/${buildingUUid}&${CASE_TAB_ENUM.CONSTRUCTION_BUILDING_PROCEDURE_V2}`;

    window.open(url, '_blank');
  };

  return (
    <>
      {constructionDataLoading && <Loader />}
      {!constructionDataLoading && (
        <Modal scrollable isOpen={props.idConstruction !== null} size={'xl'}>
          {props.readOnly ? (
            <ModalHeader toggle={() => closeModal()}>Pregled građevine</ModalHeader>
          ) : (
            <ModalHeader>{props.idConstruction === '' ? 'Unos građevine' : 'Uređivanje građevine'}</ModalHeader>
          )}
          <ModalBody>
            <FormikProvider value={formik}>
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta zahvata'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu zahvata'}
                      onItemSelect={selected => formik.setFieldValue('constructionBuildingProcedure', selected)}
                      selected={formik.values.constructionBuildingProcedure}
                      labelKey={'value'}
                      options={constructionBuildingProcedureOptionsLoading ? [] : constructionBuildingProcedureOptions}
                      disabled={props.readOnly}
                      error={formik.errors.constructionBuildingProcedure as string}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Namjena'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi namjenu'}
                      onItemSelect={selected => formik.setFieldValue('buildingPurpose', selected)}
                      selected={formik.values.buildingPurpose}
                      labelKey={'value'}
                      options={buildingPurposeOptionsLoading ? [] : buildingPurposeOptions}
                      disabled={props.readOnly}
                      error={formik.errors.buildingPurpose as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Dodatni opis namjene'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.buildingPurposeAdditionalDescription}
                      error={
                        formik.errors.buildingPurposeAdditionalDescription ? `${formik.errors.buildingPurposeAdditionalDescription}` : ''
                      }
                      onChange={event => formik.setFieldValue('buildingPurposeAdditionalDescription', event.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Skupina prema zahtjevnosti'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi skupinu prema zahtjevnosti'}
                      onItemSelect={selected => formik.setFieldValue('requirementGroup', selected)}
                      selected={formik.values.requirementGroup}
                      labelKey={'value'}
                      options={requirementGroupOptionsLoading ? [] : requirementGroupOptions}
                      disabled={props.readOnly}
                      error={formik.errors.requirementGroup as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Građevina (*.gml)'} />
                    <div className="d-flex ispu-input-wrapper">
                      <Input
                        disabled={true}
                        placeholder={formik.values?.geometryExists ? 'Spremljena je GML datoteka.' : 'Ne postoji spremljena GML datoteka.'}
                        error={
                          !props.idConstruction
                            ? 'Potrebno je prvo spremiti građevinu prije otvaranja ISPU lokatora.'
                            : !formik.values?.geometryExists
                              ? '  '
                              : ''
                        }
                      />
                      <Button
                        disabled={!props.idConstruction}
                        label="ISPU lokator"
                        onClick={() => openIspuLocator(formik.values?.uuid)}
                        color="info"
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'NKVG'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi NKVG'}
                      onItemSelect={selected => formik.setFieldValue('buildingTypeNationalClassification', selected)}
                      selected={formik.values.buildingTypeNationalClassification}
                      labelKey={'value'}
                      options={buildingTypeNationalClassificationOptionsLoading ? [] : buildingTypeNationalClassificationOptions}
                      disabled={props.readOnly}
                      error={formik.errors.buildingTypeNationalClassification as string}
                      showErrorMessage
                      clearButton
                    />
                  </Col>
                </Row>
                <hr />
                <div className={'form-header'}>Pristupačnost</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Ocjena pristupačnosti'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi ocjenu pristupačnosti'}
                      onItemSelect={selected => formik.setFieldValue('accessibilityRating', selected)}
                      selected={formik.values.accessibilityRating}
                      labelKey={'value'}
                      options={accessibilityRatingOptionsLoading ? [] : accessibilityRatingOptions}
                      disabled={props.readOnly}
                      error={formik.errors.accessibilityRating as string}
                      showErrorMessage
                      positionFixed
                      clearButton
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Primijenjene mjere'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi primijenjene mjere'}
                      onItemSelect={selected => formik.setFieldValue('accessibilityAppliedMeasure', selected)}
                      selected={formik.values.accessibilityAppliedMeasure}
                      labelKey={'value'}
                      options={accessibilityAppliedMeasureOptionsLoading ? [] : accessibilityAppliedMeasureOptions}
                      disabled={props.readOnly}
                      error={formik.errors.accessibilityAppliedMeasure as string}
                      showErrorMessage
                      positionFixed
                      clearButton
                    />
                  </Col>
                </Row>
                <hr />
                <div className={'form-header'}>Zelena infrastruktura</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu'}
                      onItemSelect={selected => formik.setFieldValue('greenInfrastructureType', selected)}
                      selected={formik.values.greenInfrastructureType}
                      labelKey={'value'}
                      options={greenInfrastructureTypeOptionsLoading ? [] : greenInfrastructureTypeOptions}
                      disabled={props.readOnly}
                      error={formik.errors.greenInfrastructureType as string}
                      showErrorMessage
                      positionFixed
                      clearButton
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Površina'} />
                    <div className="d-flex align-items-center border rounded px-2">
                      <NumericFormat
                        decimalScale={2}
                        allowNegative={false}
                        thousandSeparator={'.'}
                        decimalSeparator={','}
                        value={formik.values.greenInfrastructureArea || ''}
                        customInput={Input}
                        onValueChange={values => {
                          const { value } = values;
                          formik.setFieldValue(`greenInfrastructureArea`, value);
                        }}
                        disabled={props.readOnly}
                        className="flex-grow-1 border-0 shadow-none"
                      />
                      <span className="ms-2 text-muted">m²</span>
                    </div>
                  </Col>
                </Row>
              </div>
            </FormikProvider>
          </ModalBody>
          <ModalFooter>
            {!props.readOnly && (
              <>
                <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
                <Button
                  label={'Spremi'}
                  size={'lg'}
                  disabled={!formik.dirty || !formik.isValid}
                  isLoading={isConstructionSaving}
                  color={'info'}
                  onClick={() => formik.handleSubmit()}
                />
              </>
            )}
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default ConstructionFormV2;
